import { useTranslation } from 'react-i18next';
import { gql, useMutation } from '@apollo/client';
import {
  AiAgentsNamespaceTakeoverArgs,
  Mutation,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import { CONVERSATION_FOR_CONTACT_INTERACTION_LIST } from 'src/components/pages/conversations/components/contact-interactions/contact-interaction-list/contact-interactions-list.query';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';

const AI_AGENT_TAKEOVER = gql`
  mutation AiAgentTakeover($input: TakeoverInput!) {
    aiAgents {
      takeover(input: $input) {
        sessionIds
      }
    }
  }
`;

type ConversationTakeoverVariables = { contactId: number };

export const useConversationTakeover = () => {
  const { id: institutionId } = useCurrentInstitution();
  const showNotification = useShowNotification();
  const { t } = useTranslation(['conversations']);
  const [mutate, { loading: isTakingOver }] = useMutation<
    Pick<Mutation, 'aiAgents'>,
    AiAgentsNamespaceTakeoverArgs
  >(AI_AGENT_TAKEOVER);

  const takeover = async ({ contactId }: ConversationTakeoverVariables) => {
    const { data } = await mutate({
      variables: { input: { contactId, institutionId } },
      // The takeover clears the AI agent in charge and adds items to the conversation feed
      // (the taken-over activity, notes), so the open conversation is refetched.
      refetchQueries: [CONVERSATION_FOR_CONTACT_INTERACTION_LIST],
      awaitRefetchQueries: true,
      onError: () => {
        showNotification({
          type: 'error',
          title: t(
            'conversations:messageInput.modes.reply.messages.aiAgent.notifications.error.title',
          ),
          message: t(
            'conversations:messageInput.modes.reply.messages.aiAgent.notifications.error.message',
          ),
        });
      },
      onCompleted: () => {
        showNotification({
          type: 'success',
          title: t(
            'conversations:messageInput.modes.reply.messages.aiAgent.notifications.success.title',
          ),
          message: t(
            'conversations:messageInput.modes.reply.messages.aiAgent.notifications.success.message',
          ),
        });
      },
    });

    return data?.aiAgents.takeover.sessionIds ?? [];
  };

  return { takeover, isTakingOver };
};
